import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Check, Gift, History, PenLine, TrendingUp, Wallet, X as XIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PERMISSIONS } from '@/client/config/permissions';
import { useGate } from '@/client/hooks/useGate';
import { useHrLevelsQuery } from '@/client/api/hr/people';
import { useCompSummaryQuery, useDecideBonusMutation, useDecideCompChangeMutation } from '@/client/api/hr/comp';
import {
  BAND_BADGE,
  BONUS_KIND_LABEL,
  COMP_REASON_LABEL,
  type Bonus,
  type CompChange,
  type Employee,
} from '@/client/types/hr';
import { HrPageShell } from './components/HrPageShell';
import { EmployeeAvatar, EmployeeLink, EmptyState, Panel, RowMenu, StatusBadge } from './components/hr-ui';
import { BonusDialog, CompChangeDialog, CompHistoryDialog } from './components/CompDialogs';
import { DecisionDialog } from './components/DecisionDialog';
import { money, pay, salary, totals } from './components/comp-format';
import { formatDay } from './components/leave-format';
import { useEmployees } from './hr-store';

type Pending = { kind: 'change'; item: CompChange; approve: boolean } | { kind: 'bonus'; item: Bonus; approve: boolean };

const CompensationPage = () => {
  const summaryQ = useCompSummaryQuery();
  const employees = useEmployees();
  const levels = useHrLevelsQuery().data ?? [];
  const canManage = useGate(PERMISSIONS.comp.manage).allowed;
  const canApprove = useGate(PERMISSIONS.comp.approve).allowed;
  const decideChange = useDecideCompChangeMutation();
  const decideBonus = useDecideBonusMutation();

  const [changeFor, setChangeFor] = useState<{ employee: Employee; mode: 'record' | 'propose' } | null>(null);
  const [bonusFor, setBonusFor] = useState<Employee | null>(null);
  const [historyFor, setHistoryFor] = useState<Employee | null>(null);
  const [deciding, setDeciding] = useState<Pending | null>(null);

  const rows = summaryQ.data?.rows ?? [];
  const pending = summaryQ.data?.pending ?? { changes: [], bonuses: [] };
  const byId = useMemo(() => new Map(employees.map((e) => [e.id, e])), [employees]);
  const levelName = (id?: string | null) => levels.find((l) => l.id === id)?.code ?? '—';
  const waiting = pending.changes.length + pending.bonuses.length;

  const stats = useMemo(() => {
    const monthly = rows.filter((r) => r.current?.monthly != null).map((r) => ({ amount: r.current!.monthly as number, currency: r.current!.currency }));
    return [
      { key: 'payroll', label: 'Monthly base pay', value: totals(monthly) },
      { key: 'missing', label: 'No pay recorded', value: rows.filter((r) => !r.current).length },
      { key: 'waiting', label: 'Waiting for approval', value: waiting },
      { key: 'band', label: 'Outside their band', value: rows.filter((r) => r.band && r.band !== 'within').length },
    ];
  }, [rows, waiting]);

  const confirm = (note: string) => {
    if (!deciding) return;
    const decision = deciding.approve ? 'approve' : 'reject';
    const opts = {
      onSuccess: () => {
        toast.success(deciding.approve ? 'Approved' : 'Rejected');
        setDeciding(null);
      },
      onError: (err: unknown) => toast.error((err as Error).message || 'Could not record the decision'),
    };
    if (deciding.kind === 'change') decideChange.mutate({ id: deciding.item.id, decision, note }, opts);
    else decideBonus.mutate({ id: deciding.item.id, decision, note }, opts);
  };

  const describe = (p: Pending | null) => {
    if (!p) return '';
    const who = byId.get(p.item.employeeId)?.name ?? 'This employee';
    if (p.kind === 'change') {
      const c = p.item;
      const lvl = c.levelId ? `, to ${levelName(c.levelId)}` : '';
      return `${who}: ${COMP_REASON_LABEL[c.reason]}${lvl}, ${pay(c.amount, c.currency, c.frequency)} from ${formatDay(c.effectiveOn)}.${c.note ? ` “${c.note}”` : ''}`;
    }
    const b = p.item;
    return `${who}: ${BONUS_KIND_LABEL[b.kind]} bonus, ${money(b.amount, b.currency)}.${b.note ? ` “${b.note}”` : ''}`;
  };

  const decideButtons = (p: Omit<Pending, 'approve'>) =>
    canApprove && (
      <div className="flex gap-1.5">
        <Button size="sm" variant="outline" className="gap-1 text-red-600 hover:text-red-700" onClick={() => setDeciding({ ...p, approve: false } as Pending)}>
          <XIcon className="h-3.5 w-3.5" /> Reject
        </Button>
        <Button size="sm" className="gap-1" onClick={() => setDeciding({ ...p, approve: true } as Pending)}>
          <Check className="h-3.5 w-3.5" /> Approve
        </Button>
      </div>
    );

  return (
    <HrPageShell
      mock={false}
      title="Compensation"
      description="Base pay, raises, promotions and bonuses. One person proposes, someone else approves. Totals are per currency, never converted."
      crumbs={[{ label: 'Human Resource', href: '/admin/human-resource' }, { label: 'Compensation' }]}
      icon={Wallet}
      stats={stats}
    >
      {summaryQ.isError ? (
        <EmptyState label={`Could not load compensation: ${(summaryQ.error as Error)?.message ?? 'unknown error'}`} />
      ) : summaryQ.isLoading ? (
        <EmptyState label="Loading pay…" />
      ) : (
        <>
          {waiting > 0 && (
            <Panel title={`Waiting for approval (${waiting})`} className="mb-4">
              <ul className="divide-y divide-gray-50">
                {pending.changes.map((c) => (
                  <li key={c.id} className="flex flex-wrap items-center justify-between gap-3 py-2.5">
                    <div className="flex min-w-0 items-center gap-3">
                      <EmployeeAvatar employee={byId.get(c.employeeId)} size="sm" />
                      <div className="min-w-0">
                        <p className="text-sm">
                          <EmployeeLink employee={byId.get(c.employeeId)} /> · {COMP_REASON_LABEL[c.reason]}
                          {c.levelId && ` to ${levelName(c.levelId)}`}
                        </p>
                        <p className="text-xs text-gray-500">
                          {pay(c.amount, c.currency, c.frequency)} from {formatDay(c.effectiveOn)}
                          {c.note && ` · “${c.note}”`}
                        </p>
                      </div>
                    </div>
                    {decideButtons({ kind: 'change', item: c })}
                  </li>
                ))}
                {pending.bonuses.map((b) => (
                  <li key={b.id} className="flex flex-wrap items-center justify-between gap-3 py-2.5">
                    <div className="flex min-w-0 items-center gap-3">
                      <EmployeeAvatar employee={byId.get(b.employeeId)} size="sm" />
                      <div className="min-w-0">
                        <p className="text-sm">
                          <EmployeeLink employee={byId.get(b.employeeId)} /> · {BONUS_KIND_LABEL[b.kind]} bonus
                        </p>
                        <p className="text-xs text-gray-500">
                          {money(b.amount, b.currency)}
                          {b.payableOn && ` on ${formatDay(b.payableOn)}`}
                          {b.note && ` · “${b.note}”`}
                        </p>
                      </div>
                    </div>
                    {decideButtons({ kind: 'bonus', item: b })}
                  </li>
                ))}
              </ul>
              {!canApprove && <p className="mt-2 text-xs text-gray-500">You can see these; someone with Approve pay changes decides them.</p>}
            </Panel>
          )}

          {rows.length === 0 ? (
            <EmptyState label="No active employees." />
          ) : (
            <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-3 font-medium">Employee</th>
                    <th className="px-4 py-3 font-medium">Level</th>
                    <th className="px-4 py-3 font-medium">Base pay</th>
                    <th className="px-4 py-3 font-medium">Band</th>
                    <th className="px-4 py-3 font-medium">Last change</th>
                    <th className="px-4 py-3 font-medium">Bonuses this year</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((r) => {
                    const e = byId.get(r.employeeId);
                    if (!e) return null;
                    return (
                      <tr key={r.employeeId} className="cursor-pointer hover:bg-gray-50" onClick={() => setHistoryFor(e)}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            <EmployeeAvatar employee={e} size="sm" />
                            <div className="min-w-0">
                              <p className="truncate font-medium text-gray-900">{e.name}</p>
                              <p className="truncate text-xs text-gray-400">{e.title || e.department}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{levelName(r.levelId)}</td>
                        <td className="px-4 py-3">
                          {r.current ? (
                            <span className="text-gray-900">{salary(r.current)}</span>
                          ) : (
                            <span className="text-amber-700">Not recorded</span>
                          )}
                          {r.scheduled && (
                            <p className="text-xs text-gray-500">
                              {salary(r.scheduled)} from {formatDay(r.scheduled.effectiveOn)}
                            </p>
                          )}
                          {(r.pendingChanges > 0 || r.pendingBonuses > 0) && (
                            <p className="text-xs text-amber-700">{r.pendingChanges + r.pendingBonuses} waiting for approval</p>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {r.band ? <StatusBadge label={BAND_BADGE[r.band].label} className={BAND_BADGE[r.band].className} /> : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-4 py-3 text-gray-600">{r.lastChangeOn ? formatDay(r.lastChangeOn) : '—'}</td>
                        <td className="px-4 py-3 text-gray-600">{totals(r.bonusesThisYear)}</td>
                        <td className="px-4 py-3 text-right">
                          <RowMenu
                            label={`Pay actions for ${e.name}`}
                            items={[
                              { label: 'Pay history', icon: History, onSelect: () => setHistoryFor(e) },
                              {
                                label: r.current ? 'Correct recorded pay' : 'Record starting pay',
                                icon: PenLine,
                                hidden: !canApprove,
                                onSelect: () => setChangeFor({ employee: e, mode: 'record' }),
                              },
                              {
                                label: 'Propose raise or promotion',
                                icon: TrendingUp,
                                hidden: !canManage || !r.current,
                                onSelect: () => setChangeFor({ employee: e, mode: 'propose' }),
                              },
                              { label: 'Propose bonus', icon: Gift, hidden: !canManage, onSelect: () => setBonusFor(e) },
                            ]}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <CompChangeDialog
        open={!!changeFor}
        mode={changeFor?.mode ?? 'propose'}
        employee={changeFor?.employee ?? null}
        current={rows.find((r) => r.employeeId === changeFor?.employee.id)?.current ?? null}
        levels={levels}
        onClose={() => setChangeFor(null)}
      />
      <BonusDialog open={!!bonusFor} employee={bonusFor} onClose={() => setBonusFor(null)} />
      <CompHistoryDialog employee={historyFor} onClose={() => setHistoryFor(null)} />
      <DecisionDialog
        open={!!deciding}
        approve={deciding?.approve ?? true}
        title={deciding?.approve ? 'Approve this?' : 'Reject this?'}
        description={describe(deciding)}
        busy={decideChange.isPending || decideBonus.isPending}
        onCancel={() => setDeciding(null)}
        onConfirm={confirm}
      />
    </HrPageShell>
  );
};

export default CompensationPage;
